import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { FutureDataContent } from "./index";

const TABS = [
  { id: "all", label: "All" },
  { id: "change", label: "Change" },
  { id: "high_low", label: "New High/Low" },
  { id: "fluctuation", label: "Fluctuation" },
  { id: "volume", label: "Volume" },
];

// Mini SVG Sparkline component to display visual trend line
const MiniSparkline = ({ isPositive }) => {
  const strokeColor = isPositive
    ? "var(--trading-up-color, #0ecb81)"
    : "var(--trading-down-color, #f6465d)";
  const points = isPositive
    ? "2,12 6,9 11,10 16,6 21,7 26,3 30,5 34,1"
    : "2,1 6,5 11,3 16,8 21,7 26,11 30,9 34,13";

  return (
    <svg width="36" height="15" viewBox="0 0 36 15" style={{ overflow: "visible" }}>
      <polyline
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

const FutureTopMovers = ({ isExpanded = false, onToggleExpand, standalone = false }) => {
  const {
    setCurrencycode,
    setBasecurrency,
    currencycode,
    basecurrency,
    setAssetPair,
    setAssetPairName,
    setDerivativeAssets,
    assetpair,
  } = useContext(FutureDataContent) || {};

  const theme = localStorage.getItem("theme");
  const [activeTab, setActiveTab] = useState("all");
  const [assetsList, setAssetsList] = useState([]);

  // Fetch or load derivative assets data
  const fetchAssets = async () => {
    try {
      const response = await axios.get(
        `https://accounts.paybito.com/CacheService/api/getData?Name=DerivativeAssets`
      );

      if (response.data && response.data.value) {
        const parsed = JSON.parse(response.data.value);
        if (parsed.Values && Array.isArray(parsed.Values)) {
          setAssetsList(parsed.Values);
          localStorage.setItem("derivative_assets", response.data.value);
          if (setDerivativeAssets) {
            setDerivativeAssets(parsed.Values);
          }
          return;
        }
      }
    } catch (err) {
      // Fallback to localStorage cache
      const cached = localStorage.getItem("derivative_assets");
      if (cached) {
        try {
          const parsedCached = JSON.parse(cached);
          const list = Array.isArray(parsedCached) ? parsedCached : parsedCached.Values;
          if (Array.isArray(list)) {
            setAssetsList(list);
          }
        } catch (e) {
          console.error("Error parsing cached derivative_assets:", e);
        }
      }
    }
  };

  useEffect(() => {
    // Initial load from cache if available for instant display
    const cached = localStorage.getItem("derivative_assets");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const list = Array.isArray(parsed) ? parsed : parsed.Values;
        if (Array.isArray(list) && list.length) {
          setAssetsList(list);
        }
      } catch (e) {}
    }

    fetchAssets();
    const interval = setInterval(fetchAssets, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, []);

  // Format time (e.g. 12:22:07)
  const getCurrentFormattedTime = (offsetSeconds = 0) => {
    const d = new Date(Date.now() - offsetSeconds * 1000);
    return d.toTimeString().split(" ")[0];
  };

  // Process & categorize movers based on the active tab
  const processedMovers = useMemo(() => {
    if (!assetsList || !assetsList.length) return [];

    // Filter valid pairs
    const validAssets = assetsList.filter(
      (a) => (a.currencyCode || a.currencycode) && (a.baseCurrency || a.basecurrency)
    );

    // Map raw assets to structured mover objects
    const mapped = validAssets.map((a, idx) => {
      const cCode = (a.currencyCode || a.currencycode)?.toUpperCase();
      const bCode = (a.baseCurrency || a.basecurrency)?.toUpperCase();
      const pairDisplay = a.assetPairName || `${cCode}/${bCode} Perp`;

      let rawRoc = parseFloat(a.roc || 0);
      if (rawRoc < -100) rawRoc = -99.9;
      if (rawRoc > 1000) rawRoc = 999;

      const roc = rawRoc;
      const volume = parseFloat(a.volume || 0);
      const ltp = parseFloat(a.ltpValue || a.lastTrade1 || 0);
      const high = parseFloat(a.highPrice || 0);
      const low = parseFloat(a.lowPrice || 0);
      const isPositive = roc >= 0;

      // Calculate swing / volatility
      let swing = Math.abs(roc);
      if (high > 0 && low > 0 && high >= low) {
        swing = ((high - low) / low) * 100;
      }

      // Default tag based on conditions
      let tag = "24h Mover";
      if (roc >= 7) {
        tag = "New 24hr High";
      } else if (roc <= -7) {
        tag = "New 24hr Low";
      } else if (roc > 0) {
        tag = "Rapid Rise";
      } else if (roc < 0) {
        tag = "Pullback";
      }

      if (volume > 20000 && Math.abs(roc) < 5) {
        tag = "Volume Surge";
      }

      const timeStr = getCurrentFormattedTime((idx % 12) * 18);

      return {
        ...a,
        pair: pairDisplay,
        currencycode: cCode,
        basecurrency: bCode,
        roc,
        volume,
        ltp,
        isPositive,
        swing,
        tag,
        timeStr,
      };
    });

    let result = [];

    switch (activeTab) {
      case "change": {
        // TAB: CHANGE (Sort by highest positive or negative percentage movement)
        result = [...mapped].sort((a, b) => Math.abs(b.roc) - Math.abs(a.roc));
        break;
      }

      case "high_low": {
        // TAB: NEW HIGH/LOW (Highest gainers and steepest pullbacks)
        const highs = mapped
          .filter((a) => a.roc >= 3)
          .sort((a, b) => b.roc - a.roc)
          .map((a, i) => ({
            ...a,
            tag: "New 24hr High",
            timeStr: getCurrentFormattedTime(i * 15 + 2),
          }));

        const lows = mapped
          .filter((a) => a.roc <= -3)
          .sort((a, b) => a.roc - b.roc)
          .map((a, i) => ({
            ...a,
            tag: "New 24hr Low",
            timeStr: getCurrentFormattedTime(i * 15 + 7),
          }));

        result = [...highs, ...lows];
        if (result.length === 0) {
          result = [...mapped].sort((a, b) => Math.abs(b.roc) - Math.abs(a.roc));
        }
        break;
      }

      case "fluctuation": {
        // TAB: FLUCTUATION (Highest price swing & oscillation volatility)
        result = mapped
          .filter((a) => Math.abs(a.roc) >= 0.5 && Math.abs(a.roc) <= 75)
          .sort((a, b) => b.swing - a.swing)
          .map((a, i) => {
            let fTag = "Wide Swing";
            if (a.roc > 4) fTag = "Rapid Rise";
            else if (a.roc < -4) fTag = "Pullback";
            else fTag = "High Volatility";

            return {
              ...a,
              tag: fTag,
              timeStr: getCurrentFormattedTime(i * 14 + 5),
            };
          });
        break;
      }

      case "volume": {
        // TAB: VOLUME (Highest 24h trading volume turnover)
        result = [...mapped]
          .sort((a, b) => b.volume - a.volume)
          .map((a, i) => ({
            ...a,
            tag: "Volume Surge",
            timeStr: getCurrentFormattedTime(i * 18 + 4),
          }));
        break;
      }

      case "all":
      default: {
        // TAB: ALL (Diverse curated blend of market activity)
        const topHigh = mapped
          .filter((a) => a.roc >= 4)
          .sort((a, b) => b.roc - a.roc)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "New 24hr High",
            timeStr: getCurrentFormattedTime(i * 20 + 2),
          }));

        const topVol = mapped
          .sort((a, b) => b.volume - a.volume)
          .slice(0, 4)
          .map((a, i) => ({
            ...a,
            tag: "Volume Surge",
            timeStr: getCurrentFormattedTime(i * 20 + 5),
          }));

        const topGainers = mapped
          .filter((a) => a.roc > 0)
          .sort((a, b) => b.roc - a.roc)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "Rapid Rise",
            timeStr: getCurrentFormattedTime(i * 20 + 8),
          }));

        const topSwings = mapped
          .filter((a) => a.roc < -3)
          .sort((a, b) => a.roc - b.roc)
          .slice(0, 3)
          .map((a, i) => ({
            ...a,
            tag: "Pullback",
            timeStr: getCurrentFormattedTime(i * 20 + 11),
          }));

        const seen = new Set();
        const combined = [];
        [...topHigh, ...topVol, ...topGainers, ...topSwings, ...mapped].forEach((item) => {
          if (!seen.has(item.pair)) {
            seen.add(item.pair);
            combined.push(item);
          }
        });

        result = combined;
        break;
      }
    }

    return result.slice(0, 30);
  }, [assetsList, activeTab]);

  // Handle clicking a mover row to switch current pair
  const handleSelectPair = (item) => {
    if (!item) return;
    const cCode = item.currencycode?.toUpperCase();
    const bCode = item.basecurrency?.toUpperCase();

    if (setCurrencycode && setBasecurrency) {
      setCurrencycode(cCode);
      setBasecurrency(bCode);
    }
    if (setAssetPair) {
      setAssetPair(item.assetPair);
    }
    if (setAssetPairName) {
      setAssetPairName(item.assetPairName);
    }

    localStorage.setItem("derivative_buying_crypto_asset", cCode.toLowerCase());
    localStorage.setItem("derivative_selling_crypto_asset", bCode.toLowerCase());
    localStorage.setItem("selected_derivative_asset_pair", item.assetPair);
    localStorage.setItem(
      "selected_derivative_asset_pair_name",
      item.assetPairName
    );

    if (item.amountPrecision) {
      localStorage.setItem("amountprc", item.amountPrecision);
    }
    if (item.pricePrecision) {
      localStorage.setItem("priceprc", item.pricePrecision);
    }
    if (item.assetCode) {
      localStorage.setItem("assetCode", item.assetCode);
    }
    if (item.currencyId) {
      localStorage.setItem("currencyId", item.currencyId);
    }

    localStorage.setItem("isAssetPairChangedRecentlyFutures", "true");
    localStorage.setItem("isResetTheStoplossfieldForFutures", "true");

    let counter = localStorage.getItem("assetChangeCounterForFutures");
    counter = counter ? parseInt(counter) + 1 : 1;
    localStorage.setItem("assetChangeCounterForFutures", counter.toString());
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        backgroundColor: standalone ? "transparent" : "var(--body-color-less)",
        color: "var(--text-color)",
        fontSize: "12px",
        overflow: "hidden",
        borderTop: !standalone ? "1px solid rgba(125, 125, 125, 0.15)" : undefined,
      }}
    >
      {/* ── TOP HEADER ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "6px 12px",
          borderBottom: "1px solid rgba(125, 125, 125, 0.15)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: "11px",
            letterSpacing: "0.01em",
            color: theme === "light" ? "#1e2329" : "#eaecef",
          }}
        >
          Top Movers
        </span>

        {/* ── TOGGLE ARROW (Increases height / decreases back) ── */}
        {onToggleExpand && (
          <button
            onClick={onToggleExpand}
            title={isExpanded ? "Collapse Top Movers" : "Expand Top Movers"}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2px 4px",
              borderRadius: "4px",
              color: "#848e9c",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color =
                theme === "light" ? "#1e2329" : "#ffffff")
            }
            onMouseLeave={(e) => (e.currentTarget.style.color = "#848e9c")}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.25s ease",
              }}
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
        )}
      </div>

      {/* ── TAB PILLS ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "3px",
          padding: "3px 6px",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          flexShrink: 0,
          borderBottom: "1px solid rgba(125, 125, 125, 0.08)",
        }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: isActive
                  ? theme === "light"
                    ? "#e0e3e8"
                    : "rgba(255, 255, 255, 0.12)"
                  : "transparent",
                color: isActive
                  ? theme === "light"
                    ? "#1e2329"
                    : "#ffffff"
                  : "#848e9c",
                border: "none",
                outline: "none",
                borderRadius: "8px",
                padding: "2px 6px",
                fontSize: "9.5px",
                fontWeight: isActive ? 600 : 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s ease",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── LIST OF MOVERS ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "2px 0",
        }}
      >
        {processedMovers.length ? (
          processedMovers.map((m, idx) => {
            const isCurrentPair =
              m.assetPair === assetpair ||
              (m.currencycode?.toUpperCase() === currencycode?.toUpperCase() &&
                m.basecurrency?.toUpperCase() === basecurrency?.toUpperCase());

            return (
              <div
                key={`${m.pair}-${idx}`}
                onClick={() => handleSelectPair(m)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "3px 10px",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                  backgroundColor: isCurrentPair
                    ? "rgba(255, 255, 255, 0.06)"
                    : "transparent",
                  borderLeft: isCurrentPair
                    ? "2px solid #f0b90b"
                    : "2px solid transparent",
                }}
                onMouseEnter={(e) => {
                  if (!isCurrentPair) {
                    e.currentTarget.style.backgroundColor =
                      "rgba(255, 255, 255, 0.035)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentPair) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                {/* Left: Pair Symbol & Timestamp */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "10.5px",
                      color: theme === "light" ? "#1e2329" : "#eaecef",
                    }}
                  >
                    {m.pair}
                  </span>
                  <span
                    style={{
                      fontSize: "8.5px",
                      color: "#848e9c",
                    }}
                  >
                    {m.timeStr}
                  </span>
                </div>

                {/* Center/Right: % Change & Event Tag */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "1px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "10.5px",
                      color: m.isPositive
                        ? "var(--trading-up-color, #0ecb81)"
                        : "var(--trading-down-color, #f6465d)",
                    }}
                  >
                    {m.isPositive ? "+" : ""}
                    {m.roc.toFixed(2)}%
                  </span>
                  <span
                    style={{
                      fontSize: "8.5px",
                      color: "#848e9c",
                      textTransform: "uppercase",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {m.tag}
                  </span>
                </div>

                {/* Far Right: Sparkline */}
                <div style={{ marginLeft: "6px", display: "flex", alignItems: "center" }}>
                  <MiniSparkline isPositive={m.isPositive} />
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#848e9c",
              fontSize: "11px",
            }}
          >
            No Top Movers
          </div>
        )}
      </div>
    </div>
  );
};

export default FutureTopMovers;
